import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { GripVertical, Sparkles, Play, Pause, Volume2, VolumeX, Tv, Check } from "lucide-react";
import { ASSETS } from "@/lib/site-data";

// Draggable before/after slider — no extra deps
const BeforeAfter = ({ beforeSrc, afterSrc, beforeAlt, afterAlt }) => {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const updateFromClient = (clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPos(pct);
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      updateFromClient(cx);
    };
    const onUp = () => (dragging.current = false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      data-testid="before-after-slider"
      className="relative aspect-[4/3] md:aspect-[16/10] w-full rounded-3xl overflow-hidden border-4 border-white shadow-[0_30px_60px_-25px_rgba(26,46,32,0.5)] select-none cursor-ew-resize bg-[#0d1f15]"
      onMouseDown={(e) => {
        dragging.current = true;
        updateFromClient(e.clientX);
      }}
      onTouchStart={(e) => {
        dragging.current = true;
        updateFromClient(e.touches[0].clientX);
      }}
    >
      {/* AFTER (base layer) */}
      <img
        src={afterSrc}
        alt={afterAlt}
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
      />
      <span className="absolute top-4 right-4 z-20 px-3 py-1.5 rounded-full bg-[#EAB308] text-[#422006] text-xs font-black uppercase tracking-[0.2em]">
        After
      </span>

      {/* BEFORE clipped */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${pos}%` }}
      >
        <img
          src={beforeSrc}
          alt={beforeAlt}
          className="absolute inset-0 h-full object-cover"
          style={{ width: containerRef.current?.offsetWidth || "100%" }}
          draggable={false}
        />
        <span className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-white text-[#1A2E20] text-xs font-black uppercase tracking-[0.2em]">
          Before
        </span>
      </div>

      {/* Divider handle */}
      <div
        className="absolute top-0 bottom-0 z-10 -translate-x-1/2 pointer-events-none"
        style={{ left: `${pos}%` }}
      >
        <div className="w-1 h-full bg-white/95" />
        <button
          aria-label="Drag to compare before and after"
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 left-1/2 w-12 h-12 rounded-full bg-[#EAB308] text-[#422006] grid place-items-center shadow-[0_8px_24px_-6px_rgba(0,0,0,0.4)] pointer-events-auto"
        >
          <GripVertical className="w-5 h-5" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
};

// Synchronized interactive Video Before/After Slider
const VideoBeforeAfter = ({ beforeSrc, afterSrc }) => {
  const containerRef = useRef(null);
  const beforeVideoRef = useRef(null);
  const afterVideoRef = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);
  const [containerWidth, setContainerWidth] = useState(600);
  const [isPlaying, setIsPlaying] = useState(true);

  // Sync container width on mount & resize
  useEffect(() => {
    if (!containerRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    resizeObserver.observe(containerRef.current);
    
    // Fallback/initial check
    setContainerWidth(containerRef.current.getBoundingClientRect().width);
    
    return () => resizeObserver.disconnect();
  }, []);

  // Sync play/pause and load states on src change
  useEffect(() => {
    const v1 = beforeVideoRef.current;
    const v2 = afterVideoRef.current;
    if (!v1 || !v2) return;

    v1.muted = true;
    v2.muted = true;
    v1.loop = true;
    v2.loop = true;
    v1.playsInline = true;
    v2.playsInline = true;

    // Load and play
    v1.load();
    v2.load();

    if (isPlaying) {
      v1.play().catch(() => {});
      v2.play().catch(() => {});
    } else {
      v1.pause();
      v2.pause();
    }
  }, [isPlaying, beforeSrc, afterSrc]);

  // Prevent time drift
  useEffect(() => {
    const v1 = beforeVideoRef.current;
    const v2 = afterVideoRef.current;
    if (!v1 || !v2) return;

    const onTimeUpdate = () => {
      if (Math.abs(v1.currentTime - v2.currentTime) > 0.15) {
        v2.currentTime = v1.currentTime;
      }
    };

    v1.addEventListener("timeupdate", onTimeUpdate);
    return () => {
      v1.removeEventListener("timeupdate", onTimeUpdate);
    };
  }, [beforeSrc, afterSrc]);

  const updateFromClient = (clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPos(pct);
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      updateFromClient(cx);
    };
    const onUp = () => (dragging.current = false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  const togglePlay = (e) => {
    e.stopPropagation();
    setIsPlaying(!isPlaying);
  };

  return (
    <div
      ref={containerRef}
      className="relative aspect-[4/3] md:aspect-[16/10] w-full rounded-3xl overflow-hidden border-4 border-white shadow-[0_30px_60px_-25px_rgba(26,46,32,0.5)] select-none cursor-ew-resize bg-[#0d1f15]"
      onMouseDown={(e) => {
        dragging.current = true;
        updateFromClient(e.clientX);
      }}
      onTouchStart={(e) => {
        dragging.current = true;
        updateFromClient(e.touches[0].clientX);
      }}
    >
      {/* AFTER (base layer) */}
      <video
        ref={afterVideoRef}
        src={afterSrc}
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay
        muted
        loop
        playsInline
      />
      <span className="absolute top-4 right-4 z-20 px-3 py-1.5 rounded-full bg-[#EAB308] text-[#422006] text-xs font-black uppercase tracking-[0.2em] pointer-events-none">
        After
      </span>

      {/* BEFORE clipped */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ width: `${pos}%` }}
      >
        <video
          ref={beforeVideoRef}
          src={beforeSrc}
          className="absolute inset-0 h-full object-cover"
          style={{ width: containerWidth }}
          autoPlay
          muted
          loop
          playsInline
        />
        <span className="absolute top-4 left-4 z-20 px-3 py-1.5 rounded-full bg-white text-[#1A2E20] text-xs font-black uppercase tracking-[0.2em] pointer-events-none">
          Before
        </span>
      </div>

      {/* Divider handle */}
      <div
        className="absolute top-0 bottom-0 z-10 -translate-x-1/2 pointer-events-none"
        style={{ left: `${pos}%` }}
      >
        <div className="w-1 h-full bg-white/95" />
        <button
          aria-label="Drag to compare before and after videos"
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 left-1/2 w-12 h-12 rounded-full bg-[#EAB308] text-[#422006] grid place-items-center shadow-[0_8px_24px_-6px_rgba(0,0,0,0.4)] pointer-events-auto"
        >
          <GripVertical className="w-5 h-5" strokeWidth={2.5} />
        </button>
      </div>

      {/* Play/Pause Control overlay */}
      <button
        onClick={togglePlay}
        className="absolute bottom-4 left-4 z-20 px-3 py-1.5 rounded-xl bg-black/60 hover:bg-black/85 text-white text-xs font-bold transition flex items-center gap-1.5 cursor-pointer border border-white/10"
      >
        {isPlaying ? (
          <>
            <Pause className="w-3 h-3 text-white" /> Pause
          </>
        ) : (
          <>
            <Play className="w-3 h-3 text-white" /> Play
          </>
        )}
      </button>
    </div>
  );
};

// Video player for the Crew in Action video
const CrewInActionPlayer = ({ src }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (isPlaying) {
      v.pause();
      setIsPlaying(false);
    } else {
      v.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  return (
    <div className="relative aspect-[4/3] md:aspect-[16/10] lg:aspect-[4/5] w-full rounded-3xl overflow-hidden border-4 border-white shadow-[0_30px_60px_-25px_rgba(26,46,32,0.5)] bg-[#0d1f15]">
      <video
        ref={videoRef}
        src={src}
        className="w-full h-full object-cover"
        autoPlay
        muted={isMuted}
        loop
        playsInline
      />
      
      {/* Absolute overlay elements */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent pointer-events-none" />

      {/* Label and Info */}
      <div className="absolute bottom-5 left-5 right-5 pointer-events-none">
        <span className="px-2.5 py-1 rounded bg-[#EAB308] text-[#422006] text-[10px] font-black uppercase tracking-[0.15em]">
          Live Reel
        </span>
        <h4 className="font-['Outfit'] font-black text-white text-lg mt-2 leading-tight">
          Imperial Crew in Action
        </h4>
        <p className="text-white/80 text-xs mt-1">
          Real-time lawn maintenance and property grooming in Tallahassee.
        </p>
      </div>

      {/* Control Buttons */}
      <div className="absolute top-4 right-4 flex gap-2">
        <button
          onClick={toggleMute}
          className="p-2.5 rounded-full bg-black/50 hover:bg-black/80 text-white transition border border-white/10"
          title={isMuted ? "Unmute sound" : "Mute sound"}
        >
          {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
        <button
          onClick={togglePlay}
          className="p-2.5 rounded-full bg-black/50 hover:bg-black/80 text-white transition border border-white/10"
          title={isPlaying ? "Pause video" : "Play video"}
        >
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.08, ease: [0.2, 0.8, 0.2, 1] },
  }),
};

const PHOTOS = [
  {
    src: ASSETS.portfolioAfter,
    alt: "Freshly mulched flower bed with vibrant blooms in a Tallahassee yard",
    label: "Front-yard flower bed install",
    location: "Killearn Estates",
  },
  {
    src: ASSETS.portfolioBefore,
    alt: "Lush green hedge and trimmed shrubs along a Tallahassee property line",
    label: "Hedge & shrub shaping",
    location: "Betton Hills",
  },
];

export const Gallery = () => {
  const [activeVideoProject, setActiveVideoProject] = useState(1);
  return (
    <section
      id="work"
      data-testid="gallery-section"
      className="relative py-24 md:py-32 bg-[#EEF2EF]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-xs tracking-[0.28em] uppercase font-bold text-[#2E5C3E] mb-4 inline-flex items-center gap-2"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Our Work
          </motion.div>
          <motion.h2
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={1}
            className="font-['Outfit'] text-4xl md:text-6xl font-black tracking-tighter text-[#1A2E20] leading-[1.02]"
          >
            Real yards. Real Tallahassee neighborhoods.
          </motion.h2>
          <motion.p
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={2}
            className="mt-5 text-base md:text-lg text-[#4A6E55] leading-relaxed"
          >
            A few snapshots from recent projects across town.
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            className="lg:col-span-8 grid sm:grid-cols-2 gap-5 md:gap-6"
            data-testid="gallery-photos"
          >
            {PHOTOS.map((p, i) => (
              <figure
                key={i}
                data-testid={`gallery-photo-${i}`}
                className="group relative rounded-3xl overflow-hidden aspect-[4/5] border-4 border-white shadow-[0_24px_50px_-25px_rgba(26,46,32,0.5)]"
              >
                <img
                  src={p.src}
                  alt={p.alt}
                  className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
                />
                <div className="absolute inset-x-0 bottom-0 p-4">
                  <div className="bg-white/95 backdrop-blur rounded-xl px-3 py-2.5">
                    <div className="text-[10px] tracking-[0.18em] uppercase text-[#2E5C3E] font-bold">
                      {p.location}
                    </div>
                    <div className="text-sm font-bold text-[#1A2E20] font-['Outfit']">
                      {p.label}
                    </div>
                  </div>
                </div>
              </figure>
            ))}
          </motion.div>

          <motion.aside
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            custom={1}
            className="lg:col-span-4 grid grid-cols-1 gap-5"
          >
            <div className="rounded-3xl bg-[#2E5C3E] text-white p-6 lg:p-8 flex flex-col justify-between min-h-[260px]">
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#EAB308] mb-3">
                  By the numbers
                </div>
                <div className="font-['Outfit'] text-5xl font-black tracking-tighter">
                  500+
                </div>
                <p className="mt-2 text-sm text-white/85 leading-relaxed">
                  Tallahassee yards transformed by our crew this year alone.
                </p>
              </div>
              <div className="mt-6 pt-6 border-t border-white/15 grid grid-cols-2 gap-4 text-xs">
                <div>
                  <div className="font-['Outfit'] text-2xl font-black text-[#EAB308]">
                    24h
                  </div>
                  <div className="text-white/70 mt-1">Avg. quote turnaround</div>
                </div>
                <div>
                  <div className="font-['Outfit'] text-2xl font-black text-[#EAB308]">
                    100%
                  </div>
                  <div className="text-white/70 mt-1">Same-crew, every visit</div>
                </div>
              </div>
            </div>
          </motion.aside>
        </div>

        {/* Transformation story — Before/After comparison */}
        <div className="mt-20 md:mt-28">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="max-w-3xl mb-10"
          >
            <div className="text-xs tracking-[0.28em] uppercase font-bold text-[#2E5C3E] mb-4 inline-flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" />
              Before & After
            </div>
            <h3 className="font-['Outfit'] text-3xl md:text-5xl font-black tracking-tighter text-[#1A2E20] leading-[1.05]">
              Hidden behind overgrowth — restored in a single visit.
            </h3>
            <p className="mt-4 text-base md:text-lg text-[#4A6E55] leading-relaxed">
              Drag the slider to see the property reveal beneath the
              years-overgrown cypress hedge our crew cleared and shaped.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            custom={1}
            data-testid="gallery-before-after"
          >
            <BeforeAfter
              beforeSrc={ASSETS.transformBefore}
              afterSrc={ASSETS.transformAfter}
              beforeAlt="Before: shrine and statue completely hidden behind massive overgrown cypress hedge"
              afterAlt="After: cleared landscape with visible shrine and Imperial crew finishing the cleanup"
            />
            <p className="mt-4 text-sm text-[#4A6E55] font-medium">
              Same property, same day. Drag the handle ↔ to compare.
            </p>
          </motion.div>
        </div>

        {/* VIDEO SHOWCASE — BEFORE/AFTER VIDEOS & CREW IN ACTION */}
        <div className="mt-20 md:mt-28 pt-20 border-t border-stone-200/65">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="max-w-3xl mb-12"
          >
            <div className="text-xs tracking-[0.28em] uppercase font-bold text-[#2E5C3E] mb-4 inline-flex items-center gap-2">
              <Tv className="w-4 h-4 text-[#2E5C3E]" />
              Video Showcase
            </div>
            <h3 className="font-['Outfit'] text-3xl md:text-5xl font-black tracking-tighter text-[#1A2E20] leading-[1.05]">
              See our work in action — frame by frame.
            </h3>
            <p className="mt-4 text-base md:text-lg text-[#4A6E55] leading-relaxed">
              Experience the meticulous detail of our landscape transformations and get a live-reel look of our skilled crew working around Leon County.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-12 gap-8 items-start">
            {/* Synchronized Video Slider column */}
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeUp}
              custom={1}
              className="lg:col-span-7 space-y-6"
            >
              <div className="bg-[#1A2E20] rounded-3xl p-5 md:p-6 text-white shadow-[0_12px_30px_rgba(26,46,32,0.15)]">
                <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 border-b border-white/10 pb-5 mb-5">
                  <div>
                    <span className="text-[10px] tracking-[0.2em] font-black uppercase text-[#EAB308]">
                      Interactive Video Comparison
                    </span>
                    <h4 className="font-['Outfit'] font-black text-xl mt-1">
                      {activeVideoProject === 1 ? "Project 1: Back-yard Cleanup" : "Project 2: Front-yard Lawn Refinement"}
                    </h4>
                  </div>
                  
                  {/* Project selection tabs */}
                  <div className="flex gap-1.5 p-1 bg-white/10 rounded-xl self-start sm:self-center">
                    <button
                      onClick={() => setActiveVideoProject(1)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1 ${
                        activeVideoProject === 1
                          ? "bg-[#EAB308] text-[#422006] shadow-sm"
                          : "text-white/85 hover:text-white"
                      }`}
                    >
                      {activeVideoProject === 1 && <Check className="w-3 h-3" />} Project 1
                    </button>
                    <button
                      onClick={() => setActiveVideoProject(2)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1 ${
                        activeVideoProject === 2
                          ? "bg-[#EAB308] text-[#422006] shadow-sm"
                          : "text-white/85 hover:text-white"
                      }`}
                    >
                      {activeVideoProject === 2 && <Check className="w-3 h-3" />} Project 2
                    </button>
                  </div>
                </div>
                
                <p className="text-sm text-stone-300 leading-relaxed mb-4">
                  {activeVideoProject === 1 
                    ? "Watch how we clear thick brush, wild weeds, and overgrown sod to reveal a pristine, clean-edged lawn."
                    : "See how we transform messy borders into crisp-edged, sculptured mulch beds and shape overgrown hedges."}
                </p>

                <VideoBeforeAfter
                  key={activeVideoProject}
                  beforeSrc={activeVideoProject === 1 ? "https://customer-assets-agu9un31.emergentagent.net/job_b7707a99-3cce-4665-a736-13c43281ba92/artifacts/xozlwjjv_Before1.mp4" : "https://customer-assets-agu9un31.emergentagent.net/job_b7707a99-3cce-4665-a736-13c43281ba92/artifacts/k58rops0_Before2.mp4"}
                  afterSrc={activeVideoProject === 1 ? "https://customer-assets-agu9un31.emergentagent.net/job_b7707a99-3cce-4665-a736-13c43281ba92/artifacts/tj3799aw_After1.mp4" : "https://customer-assets-agu9un31.emergentagent.net/job_b7707a99-3cce-4665-a736-13c43281ba92/artifacts/g374zkcn_After2.mp4"}
                />
              </div>
            </motion.div>

            {/* Crew in action player column */}
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeUp}
              custom={2}
              className="lg:col-span-5"
            >
              <CrewInActionPlayer src="https://customer-assets-agu9un31.emergentagent.net/job_b7707a99-3cce-4665-a736-13c43281ba92/artifacts/dblzboc1_VID_20260801_194757.mp4" />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
